import React from 'react';
import { Tooltip } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import { GateActions, ReviewGate } from '../../services/caseWorkflowService';
import { hours } from '../CaseWorkflow/caseWorkflowVocabulary';
import { TierPill } from '../Benchmark/BenchmarkPrimitives';
import { DECISIONS_CAPTION, suggestionsOf } from './projectOutcomesVocabulary';
import { dsVar } from '../../theme/tokens';

interface DecisionsWaitingProps {
    gates: GateActions[];
    contractGates: ReviewGate[];
    onOpenGate: (gate: GateActions) => void;
}

const TIER = /^P[0-9]$/;

/**
 * One tile per review gate, in the order a case meets them.
 *
 * The figure is cases, not review threads: a re-run asked the same question
 * again, and before supersession the earlier thread stayed pending beside it.
 * Where the two differ the thread count is shown beside the case count, muted,
 * so the gap is visible without being the number.
 */
const DecisionsWaiting: React.FC<DecisionsWaitingProps> = ({ gates, contractGates, onOpenGate }) => {
    const contractByCode = new Map(contractGates.map((gate) => [gate.code, gate]));
    return (
        <section aria-label="Decisions waiting on you" className="text-left">
            <div className="flex items-center gap-2 mb-2">
                <GavelIcon sx={{ fontSize: 16, color: dsVar('accent-fg') }} />
                <h3 className="text-sm font-semibold text-fg m-0">Decisions waiting on you</h3>
                <span className="text-xs text-fg-muted truncate">{DECISIONS_CAPTION}</span>
            </div>
            <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
                {gates.map((gate) => {
                    const waiting = gate.openCases > 0;
                    const suggestions = suggestionsOf(gate, contractByCode.get(gate.code)).slice(0, 3);
                    return (
                        <button
                            key={gate.code}
                            type="button"
                            disabled={!waiting}
                            onClick={() => onOpenGate(gate)}
                            aria-label={`${gate.label}: ${gate.openCases} cases waiting`}
                            className={`text-left rounded-xl border p-4 bg-surface/60 transition-colors ${
                                waiting
                                    ? 'border-accent-fg/40 hover:border-accent-fg cursor-pointer'
                                    : 'border-line cursor-default'
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-medium text-fg-2">{gate.label}</span>
                                {gate.question && (
                                    <span className="text-[11px] text-fg-muted truncate">{gate.question}</span>
                                )}
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className={`text-2xl font-extrabold ${waiting ? 'text-fg' : 'text-fg-muted'}`}>
                                    {gate.openCases.toLocaleString()}
                                </span>
                                <span className="text-xs text-fg-muted">
                                    {gate.openCases === 1 ? 'case waiting' : 'cases waiting'}
                                </span>
                                {gate.openThreads > gate.openCases && (
                                    <Tooltip
                                        title="Pending review threads behind these cases. Re-runs before supersession left earlier threads pending; only the newest per case is shown to decide."
                                        arrow
                                        placement="top"
                                        describeChild
                                    >
                                        <span className="text-[11px] text-fg-muted cursor-help">
                                            · {gate.openThreads.toLocaleString()} threads
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                            {waiting ? (
                                <>
                                    <div className="text-xs text-fg-2 mt-1">
                                        Oldest waiting {hours(gate.items[0]?.ageHours ?? null)}
                                        {gate.p50AgeHours !== null && <span className="text-fg-muted"> · median {hours(gate.p50AgeHours)}</span>}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                        {suggestions.map((s) => (TIER.test(s.code)
                                            ? <span key={s.code} className="inline-flex items-center gap-1"><TierPill code={s.code} /><span className="text-[11px] text-fg-2">×{s.count}</span></span>
                                            : (
                                                <span key={s.code} className="rounded-full border border-line px-2 py-0.5 text-[11px] text-fg-soft">
                                                    {s.label} ×{s.count}
                                                </span>
                                            )))}
                                    </div>
                                    {gate.sarRecommended ? (
                                        <div className="text-xs text-fg-soft mt-2 font-semibold">
                                            {gate.sarRecommended.toLocaleString()} {gate.sarRecommended === 1 ? 'recommends' : 'recommend'} SAR referral
                                        </div>
                                    ) : null}
                                </>
                            ) : (
                                <div className="text-xs text-fg-muted mt-1">Nothing waiting</div>
                            )}
                        </button>
                    );
                })}
            </div>
        </section>
    );
};

export default DecisionsWaiting;
